import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { filter, delay } from 'rxjs/operators';
import { fromEvent, merge, timer } from 'rxjs';
import { take } from 'rxjs/operators';

declare let gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private readonly GA_ID = 'G-X4B9DFD7EZ';
  private isInitialized = false;
  private pendingEvents: Array<{ type: string, data: any }> = [];
  private loadingPromise: Promise<void> | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router
  ) {}

  /**
   * Initialise Google Analytics avec chargement différé intelligent
   */
  async initializeGA(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.isInitialized) {
      return;
    }

    // Attendre l'interaction utilisateur ou un délai maximum
    await this.waitForUserInteractionOrDelay();
    
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.loadGoogleAnalytics();
    return this.loadingPromise;
  }

  /**
   * Attendre l'interaction utilisateur ou 3 secondes maximum
   */
  private waitForUserInteractionOrDelay(): Promise<void> {
    return new Promise((resolve) => {
      // Événements d'interaction utilisateur
      const userEvents = ['click', 'scroll', 'keydown', 'touchstart', 'mousemove'];
      
      // Observer les interactions utilisateur
      const interaction$ = merge(
        ...userEvents.map(event => fromEvent(this.document, event))
      ).pipe(take(1));

      // Timer de 3 secondes maximum
      const timeout$ = timer(3000);

      // Résoudre dès la première interaction ou après 3 secondes
      merge(interaction$, timeout$).pipe(take(1)).subscribe(() => {
        resolve();
      });
    });
  }

  /**
   * Charge Google Analytics de manière optimisée
   */
  private async loadGoogleAnalytics(): Promise<void> {
    try {
      // Préchargement DNS
      this.preloadDNS();
      
      // Chargement du script avec délai pour éviter le blocage
      await this.loadGtagScript();
      
      // Configuration différée
      await this.delayedConfiguration();
      
      this.isInitialized = true;
      
      // Traiter les événements en attente
      this.processPendingEvents();
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de Google Analytics:', error);
    }
  }

  /**
   * Précharge les DNS pour améliorer les performances
   */
  private preloadDNS(): void {
    const dnsPrefetch = this.document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = '//www.googletagmanager.com';
    this.document.head.appendChild(dnsPrefetch);
  }

  /**
   * Charge le script gtag avec délai pour éviter le blocage
   */
  private loadGtagScript(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve(); // Ne rien faire côté serveur
    }
  
    if (!(window as any).dataLayer) {
      (window as any).dataLayer = [];
    }
  
    return new Promise((resolve, reject) => {
      if (this.document.querySelector(`script[src*="gtag/js?id=${this.GA_ID}"]`)) {
        resolve();
        return;
      }
  
      const loadScript = () => {
        const script = this.document.createElement('script');
        script.async = true;
        script.defer = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_ID}`;
  
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Impossible de charger le script Google Analytics'));
  
        this.document.head.appendChild(script);
      };
  
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(loadScript, { timeout: 2000 });
      } else {
        setTimeout(loadScript, 100);
      }
    });
  }
  

  /**
   * Configuration différée pour éviter le blocage
   */
  private async delayedConfiguration(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
  
    await new Promise(resolve => setTimeout(resolve, 100));
  
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function() {
      (window as any).dataLayer.push(arguments);
    };
  
    gtag('js', new Date());
    gtag('config', this.GA_ID, {
      send_page_view: false,
      custom_map: {},
      page_title: document.title,
      page_location: window.location.href
    });
  
    setTimeout(() => this.trackPageViews(), 200);
  }
  

  /**
   * Active le suivi automatique des changements de page
   */
  private trackPageViews(): void {
    this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
      delay(100) // Petit délai pour éviter les appels trop fréquents
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  /**
   * Suit une page vue (avec mise en queue si pas encore initialisé)
   */
  trackPageView(url: string): void {
    if (!this.isGA4Ready()) {
      this.pendingEvents.push({ type: 'pageview', data: { url } });
      return;
    }
  
    if (!isPlatformBrowser(this.platformId)) return;
  
    (window as any).gtag('config', this.GA_ID, {
      page_path: url,
      page_title: document.title
    });
  }  

  /**
   * Suit un événement personnalisé (avec mise en queue si pas encore initialisé)
   */
  trackEvent(eventName: string, parameters: any = {}): void {
    if (!this.isGA4Ready()) {
      this.pendingEvents.push({ type: 'event', data: { eventName, parameters } });
      return;
    }
  
    if (!isPlatformBrowser(this.platformId)) return;
  
    (window as any).gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label,
      value: parameters.value,
      ...parameters
    });
  }  

  /**
   * Suit une conversion (avec mise en queue si pas encore initialisé)
   */
  trackConversion(eventName: string, transactionId?: string, value?: number): void {
    const data = { eventName, transactionId, value };
  
    if (!this.isGA4Ready()) {
      this.pendingEvents.push({ type: 'conversion', data });
      return;
    }
  
    if (!isPlatformBrowser(this.platformId)) return;
  
    (window as any).gtag('event', eventName, {
      transaction_id: transactionId,
      value: value,
      currency: 'EUR'
    });
  }
  

  /**
   * Active/désactive le tracking (pour le consentement RGPD)
   */
  setTrackingConsent(granted: boolean): void {
    if (!this.isGA4Ready()) {
      this.pendingEvents.push({ type: 'consent', data: { granted } });
      return;
    }
  
    if (!isPlatformBrowser(this.platformId)) return;
  
    (window as any).gtag('consent', 'update', {
      'analytics_storage': granted ? 'granted' : 'denied',
      'ad_storage': granted ? 'granted' : 'denied'
    });
  }  

  /**
   * Traite les événements en attente
   */
  private processPendingEvents(): void {
    this.pendingEvents.forEach(event => {
      switch (event.type) {
        case 'pageview':
          this.trackPageView(event.data.url);
          break;
        case 'event':
          this.trackEvent(event.data.eventName, event.data.parameters);
          break;
        case 'conversion':
          this.trackConversion(event.data.eventName, event.data.transactionId, event.data.value);
          break;
        case 'consent':
          this.setTrackingConsent(event.data.granted);
          break;
      }
    });
    
    this.pendingEvents = [];
  }

  /**
   * Vérifie si GA4 est prêt
   */
  private isGA4Ready(): boolean {
    return isPlatformBrowser(this.platformId) && 
           this.isInitialized && 
           typeof gtag !== 'undefined';
  }

  /**
   * Force le chargement immédiat (pour les cas d'urgence)
   */
  forceLoad(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }
    
    this.loadingPromise = this.loadGoogleAnalytics();
    return this.loadingPromise;
  }
}

// Factory function optimisée pour APP_INITIALIZER
export function initializeGoogleAnalytics(gaService: GoogleAnalyticsService) {
  return () => {
    // Ne pas bloquer l'application au démarrage
    gaService.initializeGA().catch(error => {
      console.warn('Google Analytics initialization delayed:', error);
    });
    return Promise.resolve();
  };
}